import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, Ban, Bot, Database, ArrowLeft, CheckCircle, FileCheck, Scale } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { siteConfig } from "../data/mock";

const pillars = [
  {
    icon: Scale,
    title: "One-Stop Authority",
    description: "We control and administer 100% of the Master Recording and 100% of the Publishing rights. One signature clears both sides."
  },
  {
    icon: Ban,
    title: "Zero Uncleared Samples",
    description: "Every track is composed from scratch. We do not use unauthorized loops, uncleared samples, or generic commercial sample packs."
  },
  {
    icon: Bot,
    title: "100% Human-Produced",
    description: "Our catalog is strictly human-generated. We explicitly prohibit the use of AI-generated stems, generative compositional tools, or AI vocal cloning in our active pitching roster."
  },
  {
    icon: Database,
    title: "Fully Reconciled Data",
    description: "Every asset is fully registered with BMI and The Mechanical Licensing Collective (The MLC) with complete ISWC and Work Numbers assigned prior to pitching."
  }
];

const IndemnityPage = () => {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] relative overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      
      {/* Glow orb */}
      <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-[rgba(218,255,1,0.04)] rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative z-10">
        
        {/* Back Link */}
        <motion.a
          href="/"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs sm:text-sm hover:text-[rgb(218,255,1)] transition-colors mb-10 sm:mb-12"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={16} />
          Back to Home
        </motion.a>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12 sm:mb-16"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] sm:text-xs tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            CLEARANCE & INDEMNITY
          </Badge>
          
          <h1 
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            100% Cleared. <span className="text-[rgb(218,255,1)]">Zero Legal Friction.</span>
          </h1>
          
          <p 
            className="text-[rgb(218,218,218)] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            At ItzGreen Music, we understand that a massive sync placement is only as valuable as its paperwork. We operate a strict "One-Stop Shop" model designed specifically for high-volume television, film, and trailer post-production.
          </p>
        </motion.div>

        {/* Protocol Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-10 sm:mb-12"
        >
          <p 
            className="text-[rgb(161,161,170)] text-sm sm:text-base leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            When you pull an asset from the ItzGreen catalog, it is backed by our four-pillar <span className="text-[rgb(218,255,1)] font-semibold">Indemnity Protocol</span>:
          </p>
        </motion.div>

        {/* Four Pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16">
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl p-5 sm:p-6 hover:border-[rgba(218,255,1,0.3)] transition-colors relative overflow-hidden group"
            >
              {/* Accent line */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center flex-shrink-0">
                  <pillar.icon size={20} className="text-[rgb(218,255,1)]" />
                </div>
                <div>
                  <h3 
                    className="text-white text-base sm:text-lg font-bold mb-2"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    {pillar.title}
                  </h3>
                  <p 
                    className="text-[rgb(161,161,170)] text-xs sm:text-sm leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {pillar.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Closing Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-[rgba(218,255,1,0.03)] border border-[rgba(218,255,1,0.15)] rounded-xl p-6 sm:p-8 text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield size={24} className="text-[rgb(218,255,1)]" />
            <h3 
              className="text-[rgb(218,255,1)] text-lg sm:text-xl font-bold"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              THE ITZGREEN GUARANTEE
            </h3>
          </div>
          <p 
            className="text-white text-base sm:text-lg font-semibold mb-2"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            You secure the edit. We secure the liability.
          </p>
          <p 
            className="text-[rgb(161,161,170)] text-xs sm:text-sm"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Every asset in the ItzGreen catalog is pre-cleared and ready for immediate broadcast use.
          </p>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="text-center mt-12 sm:mt-16"
        >
          <p 
            className="text-[rgb(218,255,1)] text-xs sm:text-sm font-bold tracking-[0.15em] mb-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {siteConfig.tagline}
          </p>
          <p 
            className="text-[rgb(161,161,170)] text-[10px] sm:text-xs"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {siteConfig.copyright}
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default IndemnityPage;
